import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Checked, LongArrowLeft24 } from 'ui/icons';
import { ErrorStateWebError } from 'ui/illustrations';
import { Link } from 'ui/links';
import { CircleSpinner } from 'ui/spinner';
import { colors } from 'ui/theme';
import {
  Description,
  DialogContainer,
  IconContainer,
  LinkWrapper,
  TextWrapper,
  Title,
} from './Styled';

type PDFGenerationDialogProps = {
  status: 'loading' | 'success' | 'error';
  onRetry?: () => void;
  downloadLink?: string;
};

export const getContent = (
  status: PDFGenerationDialogProps['status'],
  downloadLink?: PDFGenerationDialogProps['downloadLink'],
) => {
  switch (status) {
    case 'loading':
      return {
        title: 'Hold tight, your PDF report is being generated!',
        description: 'This can take up to a minute.',
        icon: <CircleSpinner color={colors.borderStrong} />,
      };
    case 'success':
      return {
        title: 'Successfully generated your PDF report!',
        description: 'PDF will automatically open in a new tab.',
        icon: <Checked height={48} width={48} color={colors.borderSuccess} />,
        link: downloadLink,
        preLinkText: 'If not,',
        linkText: 'open PDF in new tab',
      };
    case 'error':
      return {
        title: 'Unable to generate your PDF report.',
        description: 'Please try refreshing the page or wait a few minutes and try again.',
        icon: <ErrorStateWebError />,
        retry: true,
      };
    default:
      return null;
  }
};

export const PDFGenerationDialog: React.FC<PDFGenerationDialogProps> = ({
  status,
  onRetry,
  downloadLink,
}) => {
  const content = getContent(status, downloadLink);

  if (!content) return null;

  return (
    <DialogContainer>
      <IconContainer data-icon={status}>{content.icon}</IconContainer>
      <TextWrapper>
        <Title>{content.title}</Title>
        <Description>{content.description}</Description>
        {content.link && (
          <LinkWrapper>
            {content.preLinkText}{' '}
            <Link
              href={content.link}
              hasOpenNewTabIcon
              target="_blank"
              variant="default"
              size="caption"
            >
              open PDF in new tab
            </Link>
          </LinkWrapper>
        )}
      </TextWrapper>
      {content.retry && onRetry && (
        <Button size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.SECONDARY} onClick={onRetry}>
          <LongArrowLeft24 />
          Go back
        </Button>
      )}
    </DialogContainer>
  );
};
